<template>
  <div class="coriolis-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$F_c = -2m(ω × v)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>旋转角速度 ω</label>
            <span class="val">{{ omega.toFixed(1) }} <small>rad/s</small></span>
          </div>
          <div class="hud-item orange">
            <label>参考系状态</label>
            <span class="val">{{ viewMode === 'rotating' ? '旋转系 (非惯性)' : '惯性系 (外部静止)' }}</span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 400 260">
          <defs>
             <filter id="glow-ball"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <radialGradient id="platform-grad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="#1e293b" stop-opacity="0.2" />
                <stop offset="100%" stop-color="#334155" stop-opacity="0.6" />
             </radialGradient>
          </defs>

          <!-- 旋转转盘背景 -->
          <g :transform="viewMode === 'rotating' ? '' : `rotate(${angle}, 200, 130)`">
             <circle cx="200" cy="130" r="100" fill="url(#platform-grad)" stroke="#475569" stroke-width="2" />
             <!-- 刻度线 -->
             <line v-for="n in 12" :key="'m'+n" x1="200" y1="30" x2="200" y2="45" stroke="#64748b" :transform="`rotate(${n*30}, 200, 130)`" />
             <line x1="100" y1="130" x2="300" y2="130" stroke="#1e293b" stroke-dasharray="4,4" />
             <line x1="200" y1="30" x2="200" y2="230" stroke="#1e293b" stroke-dasharray="4,4" />
          </g>

          <!-- 惯性参考路径 (小球在外部视角走的直线) -->
          <line v-if="viewMode === 'inertial'" x1="200" y1="130" x2="200" y2="30" stroke="#94a3b8" stroke-dasharray="5,5" opacity="0.3" />

          <!-- 小球轨迹 Trail (Rotating Frame Perspective) -->
          <polyline :points="trailPoints" fill="none" stroke="#f43f5e" stroke-width="2" opacity="0.6" stroke-dasharray="4,2" />

          <!-- 运动小球 -->
          <g :transform="`translate(${ballPos.x}, ${ballPos.y})`">
             <circle r="7" fill="#f43f5e" filter="url(#glow-ball)" />
             <path d="M -4,-4 L 4,4 M -4,4 L 4,-4" stroke="#fff" stroke-width="1.5" opacity="0.5" />
          </g>

          <!-- 旋转矢量指示 ω -->
          <path d="M 320,50 A 20,20 0 1 1 350,80" fill="none" stroke="#06b6d4" stroke-width="2" marker-end="url(#arr-rot)" />
          <text x="355" y="65" fill="#06b6d4" font-size="12" font-weight="900">ω</text>
        </svg>

        <div class="interaction-hint">切换“惯性系”观察真相：小球其实一直在走直线</div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center gap-4">
                 <span class="label" style="width: 100px">调节旋转速度 ω</span>
                 <el-slider v-model="omega" :min="-8" :max="8" :step="0.5" size="small" class="flex-1"></el-slider>
                 <el-button type="danger" size="mini" @click="launch">发射小球</el-button>
              </div>
              <div class="flex justify-between items-center mt-1">
                 <span class="label">切换观察视角:</span>
                 <el-radio-group v-model="viewMode" size="mini">
                    <el-radio-button label="rotating">旋转参考系</el-radio-button>
                    <el-radio-button label="inertial">惯性参考系</el-radio-button>
                 </el-radio-group>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-refresh"></i>
           <p><strong>核心揭示：</strong>科氏力是一钟“惯性力”。当你站在旋转转盘上（<strong>非惯性系</strong>），你会惊奇地发现原本射向核心的小球向一侧偏转了。但如果你跳出转盘（<strong>惯性系</strong>），你会发现小球自始至终都在沿直线匀速运动。这意味着：<strong>科氏力源于我们描述运动的坐标轴在旋转。</strong>它是地球上气旋旋转的根本原因。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CoriolisEffectViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      omega: 3,
      angle: 0,
      viewMode: 'rotating',
      ball: { active: false, r: 0, t: 0, launchAngle: 0 },
      trail: [],
      t: 0
    }
  },
  computed: {
    ballPos() {
       if (!this.ball.active) return { x: 200, y: 130 };
       
       // Calculate in inertial frame first (simple straight line)
       let x_inertial = 0;
       let y_inertial = -this.ball.r;
       
       if (this.viewMode === 'inertial') {
          return { x: 200 + x_inertial, y: 130 + y_inertial };
       } else {
          // Perspective from rotating frame: subtract the platform's accumulated rotation
          // angle_to_rotate = -current_platform_angle_relative_to_launch
          let relativeAngle = - (this.angle - this.ball.launchAngle);
          let rad = relativeAngle * Math.PI / 180;
          return {
             x: 200 + (x_inertial * Math.cos(rad) - y_inertial * Math.sin(rad)),
             y: 130 + (x_inertial * Math.sin(rad) + y_inertial * Math.cos(rad))
          };
       }
    },
    trailPoints() {
       return this.trail.map(p => `${p.x},${p.y}`).join(' ');
    }
  },
  methods: {
    launch() {
       this.ball = { active: true, r: 0, launchAngle: this.angle };
       this.trail = [];
    },
    animate() {
       this.angle = (this.angle + (this.omega * 0.5)) % 360;
       if (this.ball.active) {
          this.ball.r += 1.8;
          if (this.ball.r > 105) {
             this.ball.active = false;
          } else {
             this.trail.push({...this.ballPos});
             if (this.trail.length > 60) this.trail.shift();
          }
       }
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); }
}
</script>

<style scoped lang="scss">
.coriolis-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #f43f5e; }
    }
  }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .flex-1 { flex: 1; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }
.val-tag { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; width: 40px; text-align: right; }

.discovery-card-compact {
  background: #fff1f2; padding: 10px 14px; border-radius: 10px; border: 1px solid #ffe4e6; display: flex; align-items: center; gap: 10px;
  i { color: #f43f5e; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #9f1239; line-height: 1.5; strong { color: #881337; } }
}
</style>
